import { SideType } from "@aura-frontend/omnibus-az-client";

import { splitName } from "./splitName";

/**
 * A player read from a roster member, e.g. one of the players
 * POST /rosters/unofficial/upload just created (the members of the team it
 * returns). Members come back camelCase or snake_case depending on the record.
 */
export type UploadedPlayer = {
  externalId: string;
  /** The name as omnibus returned it ("Last, First") */
  playerName: string;
  firstName: string;
  lastName: string;
  jerseyNumber?: number;
  /** Lowercase; empty when the member has none */
  bats: string;
  throws: string;
};

const pick = (record: Record<string, unknown>, ...keys: string[]) =>
  keys.map((key) => record[key]).find((value) => value != null && value !== "");

const toJerseyNumber = (value: unknown) => {
  const jerseyNumber = parseInt(String(value ?? ""), 10);
  return Number.isNaN(jerseyNumber) ? undefined : jerseyNumber;
};

/** Null for a member without an external id. */
export const parseUploadedMember = (member: unknown): UploadedPlayer | null => {
  const record = member as Record<string, unknown>;
  const externalId = pick(record, "externalId", "external_id");
  if (typeof externalId !== "string") return null;

  const playerName = String(pick(record, "playerName", "player_name") ?? "");
  const { firstName, lastName } = splitName(playerName);
  return {
    externalId,
    playerName,
    firstName,
    lastName,
    jerseyNumber: toJerseyNumber(
      pick(record, "jerseyNumber", "jersey_number", "number"),
    ),
    bats: String(pick(record, "hitSide", "hit_side") ?? "").toLowerCase(),
    throws: String(pick(record, "pitchSide", "pitch_side") ?? "").toLowerCase(),
  };
};

export const parseUploadedMembers = (members: unknown[]) =>
  members
    .map(parseUploadedMember)
    .filter((player): player is UploadedPlayer => player !== null);

/**
 * Body entry for POST /rosters/{id}/players. Sends the player's
 * `external_id` so omnibus links the player the upload already created;
 * without it, omnibus creates them a second time.
 */
export const uploadedPlayerToRosterPlayerInfo = (player: UploadedPlayer) => ({
  first_name: player.firstName,
  last_name: player.lastName,
  bats: (player.bats || undefined) as SideType,
  throws: (player.throws || undefined) as SideType,
  external_id: player.externalId,
  ...(player.jerseyNumber !== undefined
    ? { jersey_number: player.jerseyNumber }
    : {}),
});
